import { GeckoMeasurementUnit } from '../measurement/geckoMeasurementUnit'
import { parseString } from '../shared/parsing'

/**
 * A class representing the units of a 2-dimensional space.
 */
export class GeckoComponentSpace {
  //----------------------------------------------------------------------------

  /** The Rolodex ID of the component this space is in */
  public readonly componentID: string

  /** The unit of x values over this space */
  public readonly x: {
    readonly unit: GeckoMeasurementUnit
  }

  /** The unit of y values over this space */
  public readonly y: {
    readonly unit: GeckoMeasurementUnit
  }

  /**
   * A simple constructor- private to encourage use of more descriptive static
   * factory methods like CreateFromMeasurements
   * @param componentID   The Rolodex ID of the component this space is in
   * @param xUnit         The measurement units of the x-axis
   * @param yUnit         The measurement units of the y-axis
   * @private
   */
  private constructor(
    componentID: string,
    xUnit: GeckoMeasurementUnit,
    yUnit: GeckoMeasurementUnit
  ) {
    this.componentID = componentID
    this.x = { unit: xUnit }
    this.y = { unit: yUnit }
  }

  /**
   * Factory method for creating a GeckoComponentSpace from the x measurement
   * unit and y measurement unit.
   * @param componentID   The Rolodex ID of the component this space is in
   * @param xUnit         The measurement unit of this space's x coordinates
   * @param yUnit         The measurement unit of this space's y coordinates
   * @constructor
   */
  public static CreateFromMeasurementUnits(
    componentID: string,
    xUnit: GeckoMeasurementUnit,
    yUnit: GeckoMeasurementUnit
  ) {
    return new GeckoComponentSpace(componentID, xUnit, yUnit)
  }

  /**
   * Factory method for creating a GeckoComponentSpace from a
   * JSON structured as { x_unit, y_unit }. Returns undefined
   * if the JSON is structured incorrectly.
   * @param componentID   The Rolodex ID of the component this space is in
   * @param json          JSON describing the boundaries of this rect
   * @constructor
   */
  public static CreateFromJSON(
    componentID: string,
    json: Record<string, unknown> | undefined
  ): GeckoComponentSpace | undefined {
    if (!json) return undefined

    const xUnit = GeckoMeasurementUnit.GetByID(
      parseString(json.x_measurement_unit)
    )
    const yUnit = GeckoMeasurementUnit.GetByID(
      parseString(json.y_measurement_unit)
    )
    if (xUnit === undefined || yUnit === undefined) return undefined

    return GeckoComponentSpace.CreateFromMeasurementUnits(
      componentID,
      xUnit,
      yUnit
    )
  }

  /**
   * A method returning a legible string form of this object's data
   */
  public toString(): string {
    return `(${this.x.unit}, ${this.y.unit})`
  }
}
